import { LitElement, html, svg } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  getAnalytics,
  getAnalyticsSummary,
  getBillingStatus,
  type AnalyticsResponse,
  type SummaryResponse,
  type BillingStatus,
  type DailyEntry,
  type WidgetSummary,
} from '../api';
import { router } from '../router';

// Widget types supported by the dashboard
const WIDGET_TYPES = [
  { key: 'countdown', label: 'Countdown Timer', icon: '\u23F3' },
  { key: 'sales_popup', label: 'Sales Popup', icon: '\uD83D\uDCE2' },
  { key: 'stock_counter', label: 'Stock Counter', icon: '\uD83D\uDCE6' },
  { key: 'announcement_bar', label: 'Announcement Bar', icon: '\uD83D\uDCCC' },
  { key: 'visitor_counter', label: 'Visitor Counter', icon: '\uD83D\uDC65' },
] as const;

type WidgetKey = (typeof WIDGET_TYPES)[number]['key'];

// Range options
const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
] as const;

// SVG chart dimensions
const CHART_WIDTH = 700;
const CHART_HEIGHT = 200;
const CHART_PADDING = { top: 16, right: 16, bottom: 36, left: 48 };

/** Format "2026-02-11" -> "Feb 11" */
function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Render an inline SVG bar chart for daily impressions + clicks */
function renderChart(daily: DailyEntry[]) {
  if (daily.length === 0) return svg``;

  const innerW = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;
  const innerH = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;

  const maxImpressions = Math.max(...daily.map((d) => d.impressions), 1);
  // Nice round ceiling for y-axis
  const yMax = Math.ceil(maxImpressions / 10) * 10 || 10;

  const n = daily.length;
  const groupW = innerW / n;
  const barW = Math.max(4, groupW * 0.5);
  const clickBarW = Math.max(2, barW * 0.45);

  // Y-axis tick count
  const yTicks = 4;

  const yScale = (val: number) => innerH - (val / yMax) * innerH;

  return svg`
    <svg
      viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}"
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Daily impressions and clicks bar chart"
    >
      <g transform="translate(${CHART_PADDING.left}, ${CHART_PADDING.top})">

        <!-- Y-axis grid lines and labels -->
        ${Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = Math.round((yMax / yTicks) * i);
          const y = yScale(val);
          return svg`
            <line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="#e5e7eb" stroke-width="1"/>
            <text x="-6" y="${y + 4}" text-anchor="end" font-size="11" fill="#9ca3af">${val}</text>
          `;
        })}

        <!-- Bars -->
        ${daily.map((entry, i) => {
          const cx = i * groupW + groupW / 2;
          const barH = Math.max(1, (entry.impressions / yMax) * innerH);
          const barY = innerH - barH;
          const clickH = Math.max(1, (entry.clicks / yMax) * innerH);
          const clickY = innerH - clickH;
          const label = fmtDate(entry.date);

          return svg`
            <!-- Impressions bar -->
            <rect
              x="${cx - barW / 2}"
              y="${barY}"
              width="${barW}"
              height="${barH}"
              fill="#6366f1"
              rx="2"
              opacity="0.85"
            />
            <!-- Clicks overlay bar -->
            <rect
              x="${cx - clickBarW / 2}"
              y="${clickY}"
              width="${clickBarW}"
              height="${clickH}"
              fill="#f59e0b"
              rx="2"
            />
            <!-- X-axis label -->
            <text
              x="${cx}"
              y="${innerH + 22}"
              text-anchor="middle"
              font-size="11"
              fill="#6b7280"
            >${label}</text>
          `;
        })}

        <!-- X axis line -->
        <line x1="0" y1="${innerH}" x2="${innerW}" y2="${innerH}" stroke="#d1d5db" stroke-width="1"/>
        <!-- Y axis line -->
        <line x1="0" y1="0" x2="0" y2="${innerH}" stroke="#d1d5db" stroke-width="1"/>
      </g>
    </svg>
  `;
}

@customElement('hype-analytics-page')
export class AnalyticsPage extends LitElement {
  override createRenderRoot() { return this; }

  @state() private _range = 7;
  @state() private _activeTab: WidgetKey = 'countdown';

  // Plan gating
  @state() private _billing: BillingStatus | null = null;
  @state() private _billingLoading = true;

  // Summary data
  @state() private _summaryLoading = true;
  @state() private _summaryError = '';
  @state() private _summary: SummaryResponse | null = null;

  // Per-widget analytics data
  @state() private _analyticsLoading = true;
  @state() private _analyticsError = '';
  @state() private _analytics: AnalyticsResponse | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this._checkPlanAndLoad();
  }

  private async _checkPlanAndLoad(): Promise<void> {
    this._billingLoading = true;
    try {
      this._billing = await getBillingStatus();
    } catch {
      // If billing check fails, allow access (fail open) — backend enforces anyway
      this._billing = null;
    } finally {
      this._billingLoading = false;
    }

    // Only load analytics data if plan allows it
    if (this._billing && !(this._billing.features as Record<string, boolean>).analytics) {
      return;
    }
    await this._loadAll();
  }

  private async _loadAll(): Promise<void> {
    await Promise.all([this._loadSummary(), this._loadAnalytics()]);
  }

  private async _loadSummary(): Promise<void> {
    this._summaryLoading = true;
    this._summaryError = '';
    try {
      this._summary = await getAnalyticsSummary(this._range);
    } catch (err) {
      this._summaryError = err instanceof Error ? err.message : 'Failed to load summary.';
    } finally {
      this._summaryLoading = false;
    }
  }

  private async _loadAnalytics(): Promise<void> {
    this._analyticsLoading = true;
    this._analyticsError = '';
    try {
      this._analytics = await getAnalytics(this._activeTab, this._range);
    } catch (err) {
      this._analyticsError = err instanceof Error ? err.message : 'Failed to load analytics.';
    } finally {
      this._analyticsLoading = false;
    }
  }

  private _handleRangeChange(e: Event): void {
    const target = e.currentTarget as HTMLInputElement;
    this._range = Number(target.value);
    void this._loadAll();
  }

  /** Find summary entry for a given widget type */
  private _summaryFor(widgetType: string): WidgetSummary | undefined {
    return this._summary?.widgets.find((w) => w.widget_type === widgetType);
  }

  /** Compute aggregate totals across all widgets */
  private get _totals(): { impressions: number; clicks: number; ctr: number } {
    if (!this._summary) return { impressions: 0, clicks: 0, ctr: 0 };
    const widgets = this._summary.widgets;
    const impressions = widgets.reduce((sum, w) => sum + w.impressions, 0);
    const clicks = widgets.reduce((sum, w) => sum + w.clicks, 0);
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    return { impressions, clicks, ctr };
  }

  private _renderTotalStats() {
    if (this._summaryLoading) {
      return html`
        <s-grid gridTemplateColumns="'repeat(3, 1fr)'" gap="base">
          ${[0, 1, 2].map(() => html`
            <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
              <s-stack gap="small-200">
                <s-text tone="subdued" variant="bodySm">&nbsp;</s-text>
                <s-spinner size="small"></s-spinner>
              </s-stack>
            </s-box>
          `)}
        </s-grid>
      `;
    }

    if (this._summaryError) {
      return html`
        <s-banner tone="critical">
          <strong>Failed to load summary</strong><br />${this._summaryError}
          <div style="margin-top: 0.75rem;">
            <s-button @click=${() => void this._loadSummary()}>Retry</s-button>
          </div>
        </s-banner>
      `;
    }

    const { impressions, clicks, ctr } = this._totals;
    return html`
      <s-grid gridTemplateColumns="'repeat(3, 1fr)'" gap="base">
        <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
          <s-stack gap="small-200">
            <s-text tone="subdued" variant="bodySm">Total Impressions</s-text>
            <s-text variant="headingLg" fontWeight="bold">${impressions.toLocaleString()}</s-text>
          </s-stack>
        </s-box>
        <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
          <s-stack gap="small-200">
            <s-text tone="subdued" variant="bodySm">Total Clicks</s-text>
            <s-text variant="headingLg" fontWeight="bold">${clicks.toLocaleString()}</s-text>
          </s-stack>
        </s-box>
        <s-box padding="base" background="base" borderWidth="base" borderColor="border" borderRadius="base">
          <s-stack gap="small-200">
            <s-text tone="subdued" variant="bodySm">Avg Click-Through Rate</s-text>
            <s-text variant="headingLg" fontWeight="bold">${ctr.toFixed(1)}%</s-text>
          </s-stack>
        </s-box>
      </s-grid>
    `;
  }

  private _renderWidgetTable() {
    if (this._summaryLoading || this._summaryError) return null;

    return html`
      <s-section heading="By Widget">
        <div class="an-widget-table">
          <div class="an-widget-table-header">
            <s-text variant="bodySm" tone="subdued">Widget</s-text>
            <s-text variant="bodySm" tone="subdued" class="an-num">Impressions</s-text>
            <s-text variant="bodySm" tone="subdued" class="an-num">Clicks</s-text>
            <s-text variant="bodySm" tone="subdued" class="an-num">CTR</s-text>
          </div>
          ${WIDGET_TYPES.map(({ key, label, icon }) => {
            const s = this._summaryFor(key);
            const impressions = s?.impressions ?? 0;
            const clicks = s?.clicks ?? 0;
            const ctr = s?.ctr ?? 0;
            const isActive = this._activeTab === key;
            return html`
              <button
                class="an-widget-row ${isActive ? 'an-widget-row--active' : ''}"
                @click=${() => { this._activeTab = key; void this._loadAnalytics(); }}
                aria-label="View daily breakdown for ${label}"
                aria-pressed=${isActive ? 'true' : 'false'}
              >
                <span class="an-widget-name">
                  <span class="an-widget-icon">${icon}</span>
                  <s-text fontWeight="semibold" variant="bodySm">${label}</s-text>
                </span>
                <s-text variant="headingSm" class="an-num">${impressions.toLocaleString()}</s-text>
                <s-text variant="headingSm" class="an-num">${clicks.toLocaleString()}</s-text>
                <s-text variant="headingSm" class="an-num">${ctr.toFixed(1)}%</s-text>
              </button>
            `;
          })}
        </div>
      </s-section>
    `;
  }

  private _renderChart() {
    const activeWidget = WIDGET_TYPES.find(w => w.key === this._activeTab);
    const chartHeading = activeWidget
      ? `${activeWidget.label} - Daily Breakdown`
      : 'Daily Breakdown';

    if (this._analyticsLoading) {
      return html`
        <s-section heading="${chartHeading}">
          <s-box paddingBlockStart="large" paddingBlockEnd="large">
            <s-stack direction="inline" gap="small" alignItems="center">
              <s-spinner></s-spinner>
              <s-text tone="subdued">Loading chart data...</s-text>
            </s-stack>
          </s-box>
        </s-section>
      `;
    }

    if (this._analyticsError) {
      return html`
        <s-section heading="${chartHeading}">
          <s-banner tone="critical">
            <strong>Failed to load chart data</strong><br />${this._analyticsError}
            <div style="margin-top: 0.75rem;">
              <s-button @click=${() => void this._loadAnalytics()}>Retry</s-button>
            </div>
          </s-banner>
        </s-section>
      `;
    }

    const daily = this._analytics?.daily ?? [];
    const totalImpressions = this._analytics?.totals.impressions ?? 0;

    if (totalImpressions === 0) {
      return html`
        <s-section heading="${chartHeading}">
          <s-box padding="large">
            <s-stack gap="small" alignItems="center">
              <span style="font-size:2rem;opacity:0.35">📊</span>
              <s-text fontWeight="semibold">No data yet</s-text>
              <s-text tone="subdued">Widget impressions will appear here once your widgets are live.</s-text>
            </s-stack>
          </s-box>
        </s-section>
      `;
    }

    return html`
      <s-section heading="${chartHeading}">
        <s-stack gap="base">
          <s-stack direction="inline" gap="base">
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <span class="an-legend-dot" style="background:#6366f1;opacity:0.85"></span>
              <s-text variant="bodySm" tone="subdued">Impressions</s-text>
            </s-stack>
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <span class="an-legend-dot" style="background:#f59e0b"></span>
              <s-text variant="bodySm" tone="subdued">Clicks</s-text>
            </s-stack>
          </s-stack>
          ${renderChart(daily)}
        </s-stack>
      </s-section>
    `;
  }

  private _renderUpgradePrompt() {
    return html`
      <s-page heading="Analytics">
        <s-section heading="Upgrade Required">
          <s-stack gap="base" alignItems="center">
            <s-text>Analytics is available on Growth and Pro plans. Upgrade to track impressions, clicks, and performance across all your widgets.</s-text>
            <s-button variant="primary" @click=${() => router.navigate('billing')}>View plans</s-button>
          </s-stack>
        </s-section>
      </s-page>
    `;
  }

  override render() {
    if (this._billingLoading) {
      return html`
        <s-page heading="Analytics">
          <s-section>
            <s-stack direction="inline" gap="small" alignItems="center">
              <s-spinner></s-spinner>
              <s-text tone="subdued">Loading...</s-text>
            </s-stack>
          </s-section>
        </s-page>
      `;
    }

    if (this._billing && !(this._billing.features as Record<string, boolean>).analytics) {
      return this._renderUpgradePrompt();
    }

    return html`
      <style>
        hype-analytics-page s-select { min-width: 160px; }

        /* Widget performance table */
        hype-analytics-page .an-widget-table {
          display: flex;
          flex-direction: column;
          border: 1px solid var(--s-color-border, #e3e5e7);
          border-radius: var(--s-border-radius-base, 8px);
          overflow: hidden;
        }
        hype-analytics-page .an-widget-table-header {
          display: grid;
          grid-template-columns: 1fr 100px 100px 80px;
          gap: 8px;
          padding: 8px 16px;
          background: var(--s-color-bg-surface-secondary, #f6f6f7);
          border-bottom: 1px solid var(--s-color-border, #e3e5e7);
        }
        hype-analytics-page .an-widget-row {
          display: grid;
          grid-template-columns: 1fr 100px 100px 80px;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          border-bottom: 1px solid var(--s-color-border, #e3e5e7);
          background: none;
          cursor: pointer;
          text-align: left;
          transition: background 120ms ease;
          font: inherit;
          color: inherit;
          width: 100%;
        }
        hype-analytics-page .an-widget-row:last-child {
          border-bottom: none;
        }
        hype-analytics-page .an-widget-row:hover {
          background: var(--s-color-bg-surface-secondary, #f6f6f7);
        }
        hype-analytics-page .an-widget-row--active {
          background: var(--s-color-bg-surface-secondary, #f6f6f7);
          box-shadow: inset 3px 0 0 0 #6366f1;
        }
        hype-analytics-page .an-widget-name {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        hype-analytics-page .an-widget-icon {
          font-size: 18px;
          line-height: 1;
          flex-shrink: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          background: var(--s-color-bg-surface-secondary, #f6f6f7);
        }
        hype-analytics-page .an-widget-row--active .an-widget-icon {
          background: #eef2ff;
        }
        hype-analytics-page .an-num {
          text-align: right;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        /* Legend dot */
        hype-analytics-page .an-legend-dot {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        /* Range selector row */
        hype-analytics-page .an-toolbar {
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;
        }

        /* Responsive */
        @media (max-width: 640px) {
          hype-analytics-page s-grid[gridTemplateColumns] {
            display: grid !important;
            grid-template-columns: 1fr !important;
          }
          hype-analytics-page .an-widget-table-header {
            grid-template-columns: 1fr 72px 72px 56px;
            gap: 4px;
            padding: 8px 12px;
          }
          hype-analytics-page .an-widget-row {
            grid-template-columns: 1fr 72px 72px 56px;
            gap: 4px;
            padding: 10px 12px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          hype-analytics-page .an-widget-row {
            transition: none;
          }
        }
      </style>
      <s-page heading="Analytics">
        <s-text tone="subdued">Widget performance across your storefront.</s-text>

        <div class="an-toolbar">
          <s-select
            label="Date range"
            value=${this._range}
            @change=${(e: Event) => this._handleRangeChange(e)}
          >
            ${RANGE_OPTIONS.map(
              ({ value, label }) => html`<s-option value=${value}>${label}</s-option>`
            )}
          </s-select>
        </div>

        ${this._renderTotalStats()}

        ${this._renderWidgetTable()}

        ${this._renderChart()}

      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-analytics-page': AnalyticsPage;
  }
}
